export interface WagerConfig {
    min: number
    max: number
    step: number
}

export interface ProductDefinition {
    key: string
    step: number
    title: string
    route: string
    api: 'multi-lotto' | 'pari-mutuel-lotto'
    gameType: string
    balls: number
    kind: 'grouped' | 'exact' | 'pari'
}

export const PRODUCTS: ProductDefinition[] = [
    {
        key: '5-90',
        step: 1,
        title: 'AXION 5/90',
        route: 'loto-5-90',
        api: 'multi-lotto',
        gameType: 'loto-590',
        balls: 90,
        kind: 'grouped',
    },
    {
        key: '4-80',
        step: 5,
        title: 'AXION 4/80',
        route: 'loto-4-80',
        api: 'multi-lotto',
        gameType: 'loto-4-80',
        balls: 80,
        kind: 'exact',
    },
    {
        key: '4-52',
        step: 6,
        title: 'AXION 4/52',
        route: 'loto-4-52',
        api: 'pari-mutuel-lotto',
        gameType: 'lotto-axion-4-52',
        balls: 52,
        kind: 'pari',
    },
    {
        key: '6-45',
        step: 2,
        title: 'lotto 6 of 45',
        route: 'loto-6-45',
        api: 'pari-mutuel-lotto',
        gameType: 'lotto-6-45',
        balls: 45,
        kind: 'pari',
    },
    {
        key: '4-59',
        step: 3,
        title: 'lotto 4/59',
        route: 'loto-4-59',
        api: 'pari-mutuel-lotto',
        gameType: 'loto-4-59-luckia',
        balls: 59,
        kind: 'pari',
    },
    {
        key: '5-45',
        step: 4,
        title: 'lotto 5/45',
        route: 'loto-5-45',
        api: 'pari-mutuel-lotto',
        gameType: 'lotto-5-45',
        balls: 45,
        kind: 'pari',
    },
]

const GROUP_ORDER = ['turbo', 'nap', 'chance', 'extra']

export function productByKey(key: string): ProductDefinition | undefined {
    return PRODUCTS.find((product) => product.key === key)
}

export function productByStep(step: number): ProductDefinition | undefined {
    return PRODUCTS.find((product) => product.step === step)
}

export function productByRoute(route: string): ProductDefinition | undefined {
    const value = (route || '').toLowerCase()
    return PRODUCTS.find((product) => product.route === value || product.key === value)
}

export function cartBucket(step: number): { field: string; gameType: string; api: string } {
    const product = productByStep(step) || PRODUCTS[0]
    const fields: Record<number, string> = {
        1: 'transact',
        2: 'transact645',
        3: 'transact459',
        4: 'transact545',
        5: 'transact480',
        6: 'transact452',
    }
    return {
        field: fields[product.step],
        gameType: product.gameType,
        api: product.api,
    }
}

export function classifyMultiGame(game: any): ProductDefinition | null {
    const parent = String(game?.parent?.slug || game?.parent_slug || '').toLowerCase()
    const slug = String(game?.slug || '').toLowerCase()
    const grouping = String(game?.config?.grouping || '').toLowerCase()
    if (parent.includes('4-80') || slug.includes('4-80') || grouping === '4-80') return productByKey('4-80') || null
    if (parent.includes('5-90') || slug.includes('5-90') || GROUP_ORDER.includes(grouping)) return productByKey('5-90') || null
    return null
}

export function classifyPariGame(game: any): ProductDefinition | null {
    const slug = String(game?.slug || '').toLowerCase()
    if (slug.includes('4-52')) return productByKey('4-52') || null
    if (slug === 'lotto-6-45') return productByKey('6-45') || null
    if (slug === 'lotto-5-45') return productByKey('5-45') || null
    if (slug.includes('4-59')) return productByKey('4-59') || null
    return null
}

export function visibleBetOptions(options: any[]): any[] {
    return (options || []).filter((option) => String(option?.type || option?.value || option).toLowerCase() !== 'last_7')
}

export function wagerConfigOf(game: any): WagerConfig | null {
    const wager = game?.account_game_config?.wager
    if (!wager) return null
    const min = Number(wager.min)
    const max = Number(wager.max)
    const step = Number(wager.step || 1)
    if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(step) || step <= 0) return null
    return { min, max, step }
}

export function normalizeWager(value: number, config: WagerConfig | null, fallback = 0): number {
    if (!config) return Number(value) || fallback
    let amount = Number(value)
    if (!Number.isFinite(amount)) amount = config.min
    if (amount < config.min) amount = config.min
    if (amount > config.max) amount = config.max
    if (config.step > 1) {
        const units = Math.floor((amount - config.min) / config.step)
        amount = config.min + Math.max(0, units) * config.step
        if (amount > config.max) amount = config.max - ((config.max - config.min) % config.step)
    }
    return amount
}

export function ballCountFor(game: any, product: ProductDefinition): number {
    const ballset = String(game?.config?.setup?.['1']?.ballset || '')
    const match = ballset.match(/(\d+)\s*\]/)
    if (match) return Number(match[1])
    return product.balls
}

export function isTrustedParentOrigin(origin: string, allowed: string[], selfOrigin: string): boolean {
    if (!origin) return false
    if (origin === selfOrigin) return true
    return allowed.filter(Boolean).includes(origin)
}

export function parentTargetOrigin(remembered: string, allowed: string[], selfOrigin: string): string {
    if (remembered && isTrustedParentOrigin(remembered, allowed, selfOrigin)) return remembered
    return allowed.filter(Boolean)[0] || selfOrigin
}

function variantNumber(game: any, grouping: string): string {
    return String(game?.name || '')
        .toLowerCase()
        .replace(/\s/g, '')
        .replace(String(grouping || '').toLowerCase(), '')
}

export function buildCatalogue(multiData: any[] = [], pariData: any[] = []): any[] {
    const grouped: Record<string, any[]> = {}
    const exact: Record<string, any[]> = {}

    multiData.forEach((entry) => {
        const product = classifyMultiGame(entry?.game)
        if (!product) return
        if (product.kind === 'exact') {
            exact[product.key] = exact[product.key] || []
            exact[product.key].push(entry)
            return
        }
        const name = entry.game.config?.grouping || entry.game.name
        const bucket = `${product.key}:${name}`
        grouped[bucket] = grouped[bucket] || []
        grouped[bucket].push(entry)
    })

    const cards: any[] = []
    Object.keys(grouped).forEach((bucket) => {
        const [productKey, title] = bucket.split(':')
        const product = productByKey(productKey)
        const games = grouped[bucket]
        if (!product || !games?.length) return
        const first = games[0].game
        const hasAllowed = Boolean(first.config?.allowed_picks)
        let variants: any[] = []
        if (hasAllowed) {
            variants = games
                .map((item) => ({
                    number: variantNumber(item.game, title),
                    label: item.game.name,
                    slug: item.game.slug,
                    active: false,
                    allowed_picks: item.game.config.allowed_picks,
                    price: Number(item.game.price),
                    bankers: item.game.config.bankers || [],
                    wagerConfig: wagerConfigOf(item.game),
                }))
                .sort((a, b) => Number(a.number) - Number(b.number))
        } else {
            const maximum = 5
            const minimum = maximum - games.length + 1
            variants = Array.from({ length: maximum - minimum + 1 }, (_, index) => {
                const number = minimum + index
                const match = games.find((item) => item.game.slug.endsWith(String(number)))
                return {
                    number,
                    label: match?.game?.name || String(number),
                    slug: match?.game?.slug || '',
                    active: false,
                    price: Number(match?.game?.price || first.price),
                    wagerConfig: wagerConfigOf(match?.game || first),
                }
            })
        }
        cards.push(cardFrom(product, first, title, variants, games[0]))
    })

    Object.keys(exact).forEach((productKey) => {
        const product = productByKey(productKey)
        const games = exact[productKey]
        if (!product || !games?.length) return
        const variants = games
            .map((item) => ({
                number: Number(item.game.config?.allowed_picks?.max || item.game.config?.picks?.[0] || 1),
                label: item.game.name,
                slug: item.game.slug,
                active: false,
                allowed_picks: item.game.config?.allowed_picks,
                price: Number(item.game.price),
                wagerConfig: wagerConfigOf(item.game),
            }))
            .sort((a, b) => a.number - b.number)
        cards.push(cardFrom(product, games[0].game, product.title, variants, games[0]))
    })

    pariData.forEach((entry) => {
        const product = classifyPariGame(entry?.game)
        if (!product) return
        const setup = entry.game.config?.setup?.['1'] || {}
        const picks = Number(setup.picks || product.balls)
        cards.push({
            ...baseCard(product, entry.game),
            title: entry.game.name,
            name: entry.game.slug,
            listRanDomNumber: [{ number: picks, maxPicks: Number(setup.maxPicks || picks), label: entry.game.name, slug: entry.game.slug }],
            isPariMutuelLotto: true,
            jackpot: entry.game.estimated_jackpot,
            fixedPrice: true,
        })
    })

    return cards.sort((a, b) => {
        if (a.productStep !== b.productStep) return a.productStep - b.productStep
        return GROUP_ORDER.indexOf(a.title) - GROUP_ORDER.indexOf(b.title)
    })
}

function baseCard(product: ProductDefinition, game: any) {
    return {
        id: `${product.key}-${game.slug || game.name}`,
        route: product.route,
        productKey: product.key,
        productStep: product.step,
        api: product.api,
        gameType: product.gameType,
        balls: ballCountFor(game.parent || game, product),
        parentId: game.parent?.game_id || game.game_id || null,
        preserveSlug: product.kind === 'exact',
        price: Number(game.price),
        wagerConfig: wagerConfigOf(game),
        icon: 'loto-turbo.png',
        banner: product.route,
    }
}

function cardFrom(product: ProductDefinition, game: any, title: string, variants: any[], entry: any) {
    const baseSlug = String(variants.find((variant) => variant.slug)?.slug || game.slug || '')
    return {
        ...baseCard(product, game),
        title,
        name: product.kind === 'exact' ? variants[0]?.slug : baseSlug.slice(0, -1),
        listRanDomNumber: variants,
        isPariMutuelLotto: false,
        bet_options: visibleBetOptions(game.config?.bet_options || []),
        bankers: game.config?.bankers || [],
        limitEntry: entry.game.parent?.account_limit || -1,
    }
}
